import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useOpenOnNew } from "@/hooks/useOpenOnNew";
import {
  CellContext,
  ColumnDef,
  Row,
  SortingState
} from "@tanstack/react-table";
import { Add, Bullet } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { CompanyColumn, CompanyData, CompanyStatus } from "./types";
import { getEnumTKey, monetarySortingFn } from "@/utils/strings";
import { useUsers } from "@/entities/users/hooks";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import useBoundStore from "@/services/store/BoundStore";
import CreateCompanyModal from "./components/CreateCompanyModal";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import UrlCell from "@/components/controls/DataTable/UrlCell";
import IcpFitValue from "@/components/controls/DetailedView/ICPFit/IcpFitValue";
import { getIcpFitFilterOption } from "@/components/controls/DetailedView/ICPFit/icpFitFilter";
import { IcpFit, IcpFitSubject } from "@/entities/icpFit/types";
import { ICP_FIT_COLUMN_ID } from "@/entities/icpFit/helpers";
import {
  buildGroupSummaries,
  rekeyGroupSummaries
} from "@/components/controls/DataTable/groupSummaries";
import {
  ComputedFields,
  EmptyFilters,
  GenericAggregations
} from "@/services/api/types";
import { createDataMapById, LookupNameFromIdx } from "@/utils/datamaps";
import {
  useCompaniesInfinite,
  useCompaniesDistinctFilterOptions,
  useCompanyIndustries,
  useCompanySegments,
  useResetCompaniesInfinite
} from "./hooks";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { createEnumSortingFn } from "@/utils/enums";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import {
  simpleDate,
  toCalendarDate,
  toDateOnlyISO,
  toLocalDate
} from "@/utils/datetime";
import {
  displayTotalCurrency,
  formatNumber,
  formatNumberOrFallback
} from "@/utils/numbers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { getCurrentLocale } from "@/utils/locale";
import { getLocalizedCountryOptions } from "@/utils/countries";
import { ColumnData } from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  mapSelectedCustomColumnsToTableColumns,
  getTempView,
  excludeFieldsByKey,
  ICP_FIT_FIELD_KEYS
} from "@/entities/dataTables/helpers";
import { useReconciledColumnsState } from "@/entities/dataTables/hooks";
import IconContainer from "@/components/ui/icon-container";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  COMPANIES_SORT_FIELD_BY_COLUMN,
  companyColumnKeys,
  getParentCompanyDisplayName
} from "./helpers";
import {
  COMPANY_COMPARE_COLUMN_IDS,
  COMPANY_COMPARE_TRANSLATION_KEY,
  COMPANY_DEFAULT_COMPARE_TO,
  getCompanyCompareExport,
  getCompareToLabelOverrides,
  withCompareToColumnKeys
} from "./compareToColumns";
import { resolveSortField } from "@/entities/dataTables/sorting";
import TableNameCell from "@/components/controls/TableNameCell";
import { UserData } from "@/entities/users/types";
import { useSubIndustries } from "@/entities/settings/hooks";
import { IconButton } from "@/components/ui/icon-button";
import ExportMenu from "@/components/ui/export-menu";
import { isEqual } from "lodash";
import CompaniesBottomKPIBar from "./components/CompaniesBottomKPIBar";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { OTHER_COMPANY_OPTION_VALUE } from "@/pages/leads/components/CreateLeadModal";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import { useEntityExport } from "@/services/hooks/entities/useEntityExport";
import toast from "react-hot-toast";
import { buildDeltaColumns } from "@/entities/dataTables/compareTo/deltaColumns";
import { formatTimeframeLabel } from "@/entities/dataTables/compareTo/periods";
import { useCompaniesComparison } from "@/entities/dataTables/compareTo/useCompaniesComparison";
import {
  type CompanyCompareMetricKey,
  type CompareToSelection
} from "@/entities/dataTables/compareTo/types";

type CompanyStatusColorMap = {
  [K in CompanyStatus]: string;
} & { null: string };

export const companyStatusColors: CompanyStatusColorMap = {
  [CompanyStatus.PROSPECT]: "var(--natural)",
  [CompanyStatus.CHURNED]: "var(--warning)",
  [CompanyStatus.ON_HOLD_FOR_PROSPECTING]: "var(--tertiary)",
  [CompanyStatus.ACTIVE_CUSTOMER]: "var(--charts-positive)",
  [CompanyStatus.DISQUALIFIED]: "var(--error)",
  null: "var(--border-field-default)"
};

const TABLE_ID = "companies-table";

// The column ids this table declares unconditionally. The flag-gated ones live
// outside the enum and are folded in per render — see `systemColumnIds`.
const ENUM_COMPANY_COLUMN_IDS: readonly string[] = Object.values(CompanyColumn);

const DEFAULT_COMPARE_TO = COMPANY_DEFAULT_COMPARE_TO;

interface Props {
  userData: UserData;
}
// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<CompanyData> = {
  [CompanyColumn.STATUS]: statusRank
};

/**
 * The real column the API groups its aggregations by for each of the table's
 * group options. Industry is a linked name rather than a stored value, so it
 * groups on the id it resolves from and the summaries are re-keyed to the name
 * the column shows.
 */
const AGGREGATION_FIELD_BY_GROUP_FIELD: Record<string, string> = {
  [CompanyColumn.STATUS]: CompanyColumn.STATUS,
  [CompanyColumn.SDR_OWNER]: CompanyColumn.SDR_OWNER,
  [CompanyColumn.AE_OWNER]: CompanyColumn.AE_OWNER,
  [CompanyColumn.CSM_OWNER]: CompanyColumn.CSM_OWNER,
  [CompanyColumn.SEGMENT]: CompanyColumn.SEGMENT,
  [CompanyColumn.INDUSTRY_NAME]: "industryId"
};

const Companies: React.FC<Props> = ({ userData }) => {
  const { t } = useTranslation();
  const {
    fieldName: arrOrMrrField,
    label: arrMrrLabel,
    getValue: getArrOrMrrValue
  } = useArrMrr();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [candidateData, clearCandidateData] =
    useOpenOnNew(setIsCreateModalOpen);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EmptyFilters);
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: CompanyColumn.NAME, desc: false }
  ]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [compareTo, setCompareTo] =
    useState<CompareToSelection>(DEFAULT_COMPARE_TO);

  const [viewReady, setViewReady] = useState(false);
  const isActivatingViewRef = useRef(false);
  const handleViewActivated = useCallback(() => {
    isActivatingViewRef.current = true;
    setViewReady(true);
    setTimeout(() => {
      isActivatingViewRef.current = false;
    }, 0);
  }, []);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.COMPANIES_TABLE)
  );

  const { data: entityFieldsResponse } = useEntityFields(EntityType.COMPANY);
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );
  // A flag read starts false and resolves async, so the Δ columns are withheld
  // until it has actually answered. Branching on `enabled` alone would render
  // the flag-off table first and remount it a tick later.
  const { enabled: isCompareToFlagOn, loading: compareToFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS);
  const compareToEnabled = isCompareToFlagOn && !compareToFlagLoading;

  const compareTimeframeLabel = useMemo(
    () => formatTimeframeLabel(compareTo, t),
    [compareTo, t]
  );
  const customFields = useMemo(() => {
    const fields = entityFieldsResponse?.fields || [];
    return isIcpFitEnabled
      ? fields
      : excludeFieldsByKey(fields, ICP_FIT_FIELD_KEYS);
  }, [entityFieldsResponse, isIcpFitEnabled]);
  const {
    activeFieldKeys,
    isFieldVisible,
    filterVisibleColumnKeys,
    isLoaded: fieldsLoaded
  } = useFieldVisibility(EntityType.COMPANY);
  const activeCompanyColumnKeys = useMemo(
    () => [
      ...withCompareToColumnKeys(
        filterVisibleColumnKeys(companyColumnKeys),
        compareToEnabled
      ),
      ...(isIcpFitEnabled ? [ICP_FIT_COLUMN_ID] : [])
    ],
    [filterVisibleColumnKeys, isIcpFitEnabled, compareToEnabled]
  );

  const compareToLabelOverrides = useMemo(
    () =>
      getCompareToLabelOverrides(compareToEnabled, compareTimeframeLabel, t),
    [compareToEnabled, compareTimeframeLabel, t]
  );

  const hiddenByDefaultColumnIds = useMemo(
    () =>
      new Set<string>([
        CompanyColumn.ADDRESS,
        CompanyColumn.COUNTRY,
        CompanyColumn.PHONE
      ]),
    []
  );

  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activeCompanyColumnKeys,
        customFields,
        getPageNameFromTableId(TABLE_ID),
        t,
        undefined,
        undefined,
        hiddenByDefaultColumnIds,
        compareToLabelOverrides
      ),
    [
      activeCompanyColumnKeys,
      customFields,
      t,
      hiddenByDefaultColumnIds,
      compareToLabelOverrides
    ]
  );

  const flagGatedColumnIds = useMemo(
    () => [
      ...(isIcpFitEnabled ? [ICP_FIT_COLUMN_ID] : []),
      ...(compareToEnabled ? COMPANY_COMPARE_COLUMN_IDS : [])
    ],
    [isIcpFitEnabled, compareToEnabled]
  );

  // Every column id this table declares itself, the flag-gated ones included.
  // `mapSelectedCustomColumnsToTableColumns` drops a registry field whose
  // camelCased key is already in here, so an id left out of the set renders
  // twice: once as this table's own column, once from the entity-fields
  // registry. A flag-gated id belongs here only while its flag is on — with
  // Compare-to off, `arrDelta` is a name a tenant's own field may claim.
  const systemColumnIds = useMemo(
    () => new Set<string>([...ENUM_COMPANY_COLUMN_IDS, ...flagGatedColumnIds]),
    [flagGatedColumnIds]
  );

  useReconciledColumnsState({
    columnsState,
    setColumnsState,
    defaultColumnsState,
    selectedView,
    flagGatedColumnIds,
    isReady: Boolean(entityFieldsResponse)
  });

  // A callback function to handle column changes
  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const sortParams = useMemo(() => {
    return {
      field:
        resolveSortField(sorting, COMPANIES_SORT_FIELD_BY_COLUMN) ??
        CompanyColumn.NAME,
      asc: !sorting[0].desc
    };
  }, [sorting]);

  const companiesSortByOptions = [
    CompanyColumn.NAME,
    CompanyColumn.STATUS,
    CompanyColumn.CREATED_AT
  ];
  const [groupBy, setGroupBy] = useState<string[]>([]);

  // Grouping the aggregations by the field the table groups by makes one
  // response describe every group: the server returns a row per distinct value
  // with that group's own count and sums, over the whole filtered set. A group
  // option with no entry here groups only the rows loaded so far.
  const groupField = groupBy[0];
  const aggregationsGroupField =
    AGGREGATION_FIELD_BY_GROUP_FIELD[groupField] ?? "all";
  const isServerGrouped = !!AGGREGATION_FIELD_BY_GROUP_FIELD[groupField];

  const {
    entities: unfilteredCompanies,
    hasNextPage,
    isLoading,
    isAggregationsLoading,
    isAggregationsError,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    aggregations,
    total
  } = useCompaniesInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    aggregationsGroupField,
    undefined,
    sortParams.field,
    sortParams.asc,
    undefined,
    viewReady,
    ComputedFields.MINIMAL
  );
  const resetCompaniesInfinite = useResetCompaniesInfinite();

  // Get selected columns from columnsState (updated by handleColumnsChange).
  // Fall back to temp view config if columnsState is empty (initial load) so
  // export isn't gated on the table's onColumnsChange callback having fired yet.
  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  // The export computes the Δ and Impact columns server-side for the period
  // sent in its filter, so they export while the flag is open; with it closed
  // they stay excluded, since a saved view built while it was open keeps them.
  const compareExport = useMemo(
    () => getCompanyCompareExport(compareToEnabled, compareTo, t),
    [compareToEnabled, compareTo, t]
  );

  const { exportTo: exportCompanies, isExporting: isExportingCompanies } =
    useEntityExport({
      createRoute: "companies/export/create",
      statusRoute: "companies/export/status",
      tableName: "companies",
      filters,
      sorting,
      searchTerm,
      columnsState: selectedColumns,
      totalRows: total,
      sortFieldByColumn: COMPANIES_SORT_FIELD_BY_COLUMN,
      getFilterOptions: () => companiesFilterOptions,
      excludeFromExport: compareExport.excludedColumnIds,
      getExtraColumnValueLabels: () => compareExport.impactValueLabels,
      getExtraFilterParams: compareExport.getFilterParams,
      // The live Δ headers, which name the selected period. The label saved in
      // columnsState is whichever period was selected when the layout was
      // seeded, and would title the file's column with the wrong one.
      getColumnLabelOverrides: () =>
        compareExport.pickColumnLabels(
          [...compareToLabelOverrides].map(([id, label]) => ({ id, label }))
        )
    });

  const companies = useMemo(
    () =>
      unfilteredCompanies.filter(
        (company) => company.id !== OTHER_COMPANY_OPTION_VALUE
      ),
    [unfilteredCompanies]
  );

  const companyIds = useMemo(
    () => companies.map((company) => company.id),
    [companies]
  );

  const {
    byRowId: comparisonsByCompanyId,
    notFoundIds: comparisonNotFoundIds,
    periodStart: comparisonPeriodStart,
    isError: isComparisonError
  } = useCompaniesComparison(companyIds, compareTo, compareToEnabled);

  // A Set so the column builder's per-cell lookup is O(1). Plain identity
  // dependency: `notFoundIds` and `byRowId` are members of one object returned
  // from a single useMemo in the hook, so they change identity together, and
  // `byRowId` is a fresh Map on every recompute. Since the columns memo depends
  // on both, content-keying this one could not reduce rebuilds — and while the
  // hook's memo holds, this array is already referentially stable.
  const comparisonNotFoundIdSet = useMemo(
    () => new Set(comparisonNotFoundIds),
    [comparisonNotFoundIds]
  );

  // Without this the Δ cells are blank on failure, which is exactly how "this
  // company has no prior record" renders — a failed request would read as a
  // book that did not move.
  useEffect(() => {
    if (!isComparisonError) return;
    toast.error(t(`${COMPANY_COMPARE_TRANSLATION_KEY}.loadFailed`));
  }, [isComparisonError, t]);

  const ownerIds = useMemo(() => {
    const ids = new Set<string>();
    companies.forEach((company) => {
      if (company.sdrOwner) ids.add(company.sdrOwner);
      if (company.aeOwner) ids.add(company.aeOwner);
      const csmOwner = (company as CompanyData & { csmOwner?: string | null })
        .csmOwner;
      if (csmOwner) ids.add(csmOwner);
    });
    // Grouping by an owner names a group per owner in the whole filtered set,
    // so the names have to be fetched for owners whose rows are not loaded yet.
    if (
      aggregationsGroupField === CompanyColumn.SDR_OWNER ||
      aggregationsGroupField === CompanyColumn.AE_OWNER ||
      aggregationsGroupField === CompanyColumn.CSM_OWNER
    ) {
      (
        (aggregations as GenericAggregations<string> | undefined)?.data ?? []
      ).forEach((group) => {
        if (group.fieldValue) ids.add(group.fieldValue);
      });
    }
    return Array.from(ids);
  }, [companies, aggregations, aggregationsGroupField]);
  const { users: ownerUsers } = useUsers(ownerIds);
  const userFilterValues = useUserFilterValues();
  const usersById = useMemo(() => createDataMapById(ownerUsers), [ownerUsers]);

  const { data: stateRegionsData } = useCompaniesDistinctFilterOptions(
    CompanyColumn.STATE_REGION
  );
  const stateRegionsValues = stateRegionsData?.values || [];

  const countryOptions = useMemo(
    () => getLocalizedCountryOptions(getCurrentLocale()),
    []
  );

  const industries = useBoundStore(
    (state) => state.lookup.data.companyIndustries
  );

  const subIndustries = useSubIndustries().data ?? [];
  // The `?? []` fallback is a new array on every render, and the group
  // summaries below are keyed off these - without a stable empty case they
  // recompute on every render while the lookups are still loading.
  const industriesData = useCompanyIndustries().data?.industries;
  const segmentsData = useCompanySegments().data?.segments;
  const companyIndustries = useMemo(
    () => industriesData ?? [],
    [industriesData]
  );
  const companySegments = useMemo(() => segmentsData ?? [], [segmentsData]);

  const groupSummaryFields = useMemo(
    () => [arrOrMrrField, "annualContractValue", "totalContractValue"],
    [arrOrMrrField]
  );

  const industryNameById = useMemo(() => {
    const byId: Record<string, string> = {};
    companyIndustries.forEach((industry) => {
      byId[String(industry.id)] = industry.name;
    });
    return byId;
  }, [companyIndustries]);

  const groupSummaries = useMemo(() => {
    if (!isServerGrouped) return undefined;
    const byFieldValue = buildGroupSummaries(
      aggregations as GenericAggregations<string> | undefined,
      groupSummaryFields,
      isAggregationsLoading
    );
    // The industry column shows the name, so that is the value the table groups
    // rows by — the summaries the server keyed by id have to follow.
    if (groupField === CompanyColumn.INDUSTRY_NAME) {
      return rekeyGroupSummaries(
        byFieldValue,
        (industryId) => industryNameById[industryId] ?? industryId
      );
    }
    return byFieldValue;
  }, [
    aggregations,
    isServerGrouped,
    groupSummaryFields,
    isAggregationsLoading,
    groupField,
    industryNameById
  ]);

  // Status and segment are account configuration, so every one of them is on
  // screen from the first paint — including those the filtered set has no rows
  // for. Owner groups come from the response itself, which already lists every
  // owner the filtered set contains.
  const knownGroups = useMemo(() => {
    if (!groupSummaries) return undefined;
    if (groupField === CompanyColumn.STATUS) {
      return Object.values(CompanyStatus)
        .filter((value): value is CompanyStatus => typeof value === "number")
        .map((status) => ({
          value: status,
          label: t(
            getEnumTKey("companies", "statusValues", CompanyStatus, status)
          )
        }));
    }
    if (groupField === CompanyColumn.SEGMENT) {
      return companySegments.map((segment) => ({
        value: segment.id,
        label: segment.name
      }));
    }
    if (groupField === CompanyColumn.INDUSTRY_NAME) {
      // An id the catalog no longer resolves keeps its own key, which matches
      // no group — listing it would name a group nobody can read.
      const names = new Set(Object.values(industryNameById));
      return Object.keys(groupSummaries)
        .filter((key) => key === "" || names.has(key))
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({
          value: name,
          label: name || t("tables.notAvailable")
        }));
    }
    // The blank group is "no owner", which every sibling table labels the same
    // way — distinct from an owner id the directory no longer resolves, which
    // keeps the not-found label.
    return Object.keys(groupSummaries).map((ownerId) => ({
      value: ownerId,
      label: ownerId
        ? usersById[ownerId]?.displayName || t("notFound")
        : t("tables.notAvailable")
    }));
  }, [
    groupField,
    groupSummaries,
    companySegments,
    industryNameById,
    usersById,
    t
  ]);

  useEffect(() => {
    const tempView = getTempView(selectedView);
    if (tempView?.config?.filters) {
      setFilters(tempView.config.filters);
    }
    if (tempView?.config?.searchTerm) {
      setSearchTerm(tempView.config.searchTerm);
    }
    if (tempView?.config?.groupBy) {
      setGroupBy(tempView.config.groupBy);
    }
  }, [selectedView]);

  // Clear columnsState on view switch so selectedColumns falls back to the
  // new view's persisted config until the table's onColumnsChange callback
  // reconciles it — otherwise selectedColumns briefly returns the previous
  // view's columns during the transition (stale export payload).
  useEffect(() => {
    setColumnsState([]);
  }, [selectedView]);

  const resetPage = () => resetCompaniesInfinite();

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  };

  const onRowClick = useCallback(
    (company: CompanyData) => {
      newOpenDrawer(company.id, EntityType.COMPANY);
    },
    [newOpenDrawer]
  );

  const companiesFilterOptions = useMemo<DataTableFilter[]>(
    () => [
      {
        key: CompanyColumn.NAME,
        filterType: { type: "text" }
      },
      {
        // Picked from the company list rather than typed, the way every other
        // company reference is filtered: the key is the foreign key, so the
        // payload is an id list the API matches on an indexed column.
        key: CompanyColumn.PARENT_COMPANY,
        filterType: {
          type: "map",
          values: [],
          sort: true,
          showSearch: true,
          entity: EntityType.COMPANY
        }
      },
      {
        key: CompanyColumn.CITY,
        filterType: { type: "text" }
      },
      {
        key: CompanyColumn.POSTAL_CODE,
        filterType: { type: "text" }
      },
      {
        key: CompanyColumn.ADDRESS,
        filterType: { type: "text" },
        customFieldLabel: t(`companies.${CompanyColumn.ADDRESS}`)
      },
      {
        key: CompanyColumn.COUNTRY,
        filterType: {
          type: "map",
          values: countryOptions.map(({ code, label }) => ({
            value: code,
            label
          })),
          sort: true,
          showSearch: true
        }
      },
      {
        key: CompanyColumn.PHONE,
        filterType: { type: "text" },
        customFieldLabel: t(`companies.${CompanyColumn.PHONE}`)
      },
      {
        key: CompanyColumn.STATUS,
        excludeNoValue: true,
        filterType: {
          type: "enum",
          values: {
            enumType: CompanyStatus,
            entityType: "companies",
            valueType: "statusValues",
            getIcon: (status: CompanyStatus) => (
              <IconContainer
                size="xs"
                style={{ color: companyStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      },
      ...(activeFieldKeys.has(CompanyColumn.SDR_OWNER)
        ? [
            {
              key: CompanyColumn.SDR_OWNER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      ...(activeFieldKeys.has(CompanyColumn.AE_OWNER)
        ? [
            {
              key: CompanyColumn.AE_OWNER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      ...(activeFieldKeys.has(CompanyColumn.CSM_OWNER)
        ? [
            {
              key: CompanyColumn.CSM_OWNER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      {
        key: "industryId",
        filterType: {
          type: "map",
          values: companyIndustries.map((industry) => ({
            value: industry.id,
            label: industry.name
          })),
          sort: true
        }
      },
      {
        key: CompanyColumn.SEGMENT,
        filterType: {
          type: "map",
          values: companySegments.map((segment) => ({
            value: segment.id,
            label: segment.name
          })),
          sort: true
        }
      },
      {
        key: CompanyColumn.STATE_REGION,
        filterType: {
          type: "map",
          values: stateRegionsValues?.map((role: string) => ({
            value: role,
            label: role
          })),
          sort: true
        }
      },
      {
        key: CompanyColumn.NUM_OF_EMPLOYEES,
        filterType: { type: "numeric" }
      },
      {
        key: CompanyColumn.CLOSEST_CONTRACT_END_DATE,
        filterType: { type: "date" }
      },
      {
        key: CompanyColumn.STATUS_CHANGED_AT,
        filterType: { type: "date" }
      },
      ...(isIcpFitEnabled ? [getIcpFitFilterOption(ICP_FIT_COLUMN_ID)] : [])
    ],
    [
      companyIndustries,
      companySegments,
      stateRegionsValues,
      countryOptions,
      industries,
      activeFieldKeys,
      userFilterValues,
      isIcpFitEnabled,
      t
    ]
  );

  const columns = useMemo(() => {
    // Δ columns exist only while the flag is open, so nothing downstream —
    // the picker, the saved-view merge, the export — can see them otherwise.
    // Placed next to the metric they describe, per the design.
    const companyDeltas = (
      metricKey: CompanyCompareMetricKey
    ): ColumnDef<CompanyData, unknown>[] =>
      compareToEnabled && (!fieldsLoaded || isFieldVisible(metricKey))
        ? buildDeltaColumns<CompanyData>({
            metricKey,
            // The column id and the wire key are the same string on this
            // table; CompanyColumn's values already match the response keys.
            columnId: metricKey,
            metricLabel: t(`companies.${metricKey}`),
            timeframe: compareTimeframeLabel,
            periodStart: comparisonPeriodStart,
            // The endpoint always measures against live values, so the window
            // closes today. Read inside the memo rather than as a dependency:
            // a fresh Date every render would rebuild every column.
            periodEnd: toDateOnlyISO(new Date()),
            byRowId: comparisonsByCompanyId,
            notFoundRowIds: comparisonNotFoundIdSet,
            // `companies` carries no terms-currency columns, so there is no
            // second basis to render and no currency change to withhold for.
            isTermsCurrency: false,
            translationKey: COMPANY_COMPARE_TRANSLATION_KEY,
            includeImpact: true,
            // New is specified for Contracts and Services only, and nothing
            // on a company row could establish it anyway: its figures are
            // summed from contracts whose eligibility has no history.
            allowNew: false,
            t
          })
        : [];

    const columnDefs: ColumnDef<CompanyData, any>[] = [
      {
        accessorKey: CompanyColumn.NAME,
        header: t(`companies.${CompanyColumn.NAME}`),
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.COMPANY} />
        )
      },
      {
        accessorKey: CompanyColumn.PARENT_COMPANY,
        header: t(`companies.${CompanyColumn.PARENT_COMPANY}`),
        cell: (info) =>
          getParentCompanyDisplayName(info.row.original, t("notFound"))
      },
      {
        accessorKey: CompanyColumn.WEBSITE,
        header: t(`companies.${CompanyColumn.WEBSITE}`),
        cell: (info) => {
          const website = info.getValue() as string | null | undefined;
          return website ? <UrlCell url={website} /> : t("tables.notAvailable");
        }
      },
      {
        accessorKey: CompanyColumn.STATUS,
        header: t(`companies.${CompanyColumn.STATUS}`),
        sortingFn: createEnumSortingFn(CompanyStatus),
        minSize: 200,
        cell: (info) => {
          const valueKey = getEnumTKey(
            "companies",
            "statusValues",
            CompanyStatus,
            info.getValue() as CompanyStatus
          );
          const statusKey = info.getValue() as CompanyStatus;
          return (
            <StatusBadge statusColor={companyStatusColors[statusKey]}>
              {t(valueKey)}
            </StatusBadge>
          );
        }
      },
      {
        accessorKey: CompanyColumn.SDR_OWNER,
        header: t(`companies.${CompanyColumn.SDR_OWNER}`),
        cell: (info) =>
          usersById[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        accessorKey: CompanyColumn.AE_OWNER,
        header: t(`companies.${CompanyColumn.AE_OWNER}`),
        cell: (info) =>
          usersById[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        accessorKey: CompanyColumn.CSM_OWNER,
        header: t(`companies.${CompanyColumn.CSM_OWNER}`),
        cell: (info) =>
          usersById[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        accessorKey: CompanyColumn.INDUSTRY_NAME,
        header: t(`companies.${CompanyColumn.INDUSTRY_NAME}`),
        cell: (info) => info.getValue(),
        meta: { filterKey: "industryId" }
      },
      ...(subIndustries?.length > 0
        ? [
            {
              accessorKey: CompanyColumn.SUB_INDUSTRY_NAME,
              header: t(`companies.${CompanyColumn.SUB_INDUSTRY_NAME}`),
              cell: (info: CellContext<CompanyData, any>) => info.getValue()
            }
          ]
        : []),
      {
        accessorKey: CompanyColumn.SEGMENT,
        header: t(`companies.${CompanyColumn.SEGMENT}`),
        sortingFn: createEnumSortingFn(
          companySegments.reduce(
            (acc, { id, name }) => ({
              ...acc,
              [name]: id
            }),
            {}
          )
        ),
        cell: (info) =>
          LookupNameFromIdx(
            t,
            "companies.segmentValues",
            companySegments,
            info.getValue() as number
          )
      },
      {
        accessorKey: CompanyColumn.CITY,
        header: t(`companies.${CompanyColumn.CITY}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.STATE_REGION,
        header: t(`companies.${CompanyColumn.STATE_REGION}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.POSTAL_CODE,
        header: t(`companies.${CompanyColumn.POSTAL_CODE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.ADDRESS,
        header: t(`companies.${CompanyColumn.ADDRESS}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.COUNTRY,
        header: t(`companies.${CompanyColumn.COUNTRY}`),
        cell: (info) => {
          const code = info.getValue() as string | null;
          if (!code) return t("tables.notAvailable");
          return (
            countryOptions.find((option) => option.code === code)?.label ?? code
          );
        }
      },
      {
        accessorKey: CompanyColumn.PHONE,
        header: t(`companies.${CompanyColumn.PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.NUM_OF_EMPLOYEES,
        sortingFn: "basic",
        header: t(`companies.${CompanyColumn.NUM_OF_EMPLOYEES}`),
        cell: (info) =>
          formatNumberOrFallback(
            info.getValue() as number | null | undefined,
            t("tables.notAvailable"),
            0,
            false,
            false,
            true,
            getCurrentLocale(),
            true
          )
      },
      {
        accessorKey: CompanyColumn.CREATED_AT,
        header: t(`tables.${CompanyColumn.CREATED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.STATUS_CHANGED_AT,
        header: t(`tables.${CompanyColumn.STATUS_CHANGED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        accessorKey: CompanyColumn.ARR,
        header: t(`companies.${CompanyColumn.ARR}`),
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum"
        },
        cell: (info: CellContext<any, any>) => {
          const { value, currencySign } = formatNumber(
            (info.getValue() as number) || 0,
            0,
            true,
            false
          );
          return <span>{`${currencySign}${value}`}</span>;
        }
      },
      ...companyDeltas("arr"),
      {
        accessorKey: CompanyColumn.MRR,
        header: t(`companies.${CompanyColumn.MRR}`),
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum"
        },
        cell: (info: CellContext<any, any>) => {
          const { value, currencySign } = formatNumber(
            (info.getValue() as number) || 0,
            0,
            true,
            false
          );
          return <span>{`${currencySign}${value}`}</span>;
        }
      },
      ...companyDeltas("mrr"),
      {
        accessorKey: CompanyColumn.ANNUAL_CONTRACT_VALUE,
        header: t(`companies.${CompanyColumn.ANNUAL_CONTRACT_VALUE}`),
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum"
        },
        cell: (info: CellContext<any, any>) => {
          const { value, currencySign } = formatNumber(
            (info.getValue() as number) || 0,
            0,
            true,
            false
          );
          return <span>{`${currencySign}${value}`}</span>;
        }
      },
      ...companyDeltas("annualContractValue"),
      {
        accessorKey: CompanyColumn.TOTAL_CONTRACT_VALUE,
        header: t(`companies.${CompanyColumn.TOTAL_CONTRACT_VALUE}`),
        sortingFn: monetarySortingFn,
        meta: {
          aggregate: "sum"
        },
        cell: (info: CellContext<any, any>) => {
          const { value, currencySign } = formatNumber(
            (info.getValue() as number) || 0,
            0,
            true,
            false
          );
          return <span>{`${currencySign}${value}`}</span>;
        }
      },
      ...companyDeltas("totalContractValue"),
      {
        accessorKey: CompanyColumn.CLOSEST_CONTRACT_END_DATE,
        header: t(`companies.${CompanyColumn.CLOSEST_CONTRACT_END_DATE}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toLocalDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      // Match the custom-field pattern: include the ColumnDef only when the
      // column is toggled visible, so add/remove from Display remounts the
      // column (visibility-only toggling doesn't refresh the view). Kept while
      // it is the grouping target, as custom fields are.
      ...(isIcpFitEnabled &&
      (groupBy?.includes(ICP_FIT_COLUMN_ID) ||
        selectedColumns.some(
          (col: ColumnData) => col.id === ICP_FIT_COLUMN_ID && col.isVisible
        ))
        ? [
            {
              accessorKey: ICP_FIT_COLUMN_ID,
              header: t("companies.icpFit"),
              sortingFn: "basic" as const,
              cell: (info: CellContext<CompanyData, unknown>) => (
                <IcpFitValue
                  value={info.getValue() as IcpFit | null}
                  subject={IcpFitSubject.COMPANY}
                  compact
                />
              )
            }
          ]
        : []),
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        systemColumnIds,
        groupBy
      )
    ];
    return !fieldsLoaded
      ? columnDefs
      : columnDefs.filter((col) => {
          const key = (
            "accessorKey" in col ? col.accessorKey : col.id
          ) as string;
          return isFieldVisible(key);
        });
  }, [
    companies,
    companyIndustries,
    usersById,
    selectedColumns,
    customFields,
    systemColumnIds,
    isFieldVisible,
    fieldsLoaded,
    t,
    groupBy,
    isIcpFitEnabled,
    countryOptions,
    compareToEnabled,
    compareTimeframeLabel,
    comparisonPeriodStart,
    comparisonsByCompanyId,
    comparisonNotFoundIdSet
  ]);

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  return (
    <div data-test="companies-page" className="h-full">
      <CompaniesBottomKPIBar
        aggregations={aggregations}
        totalCompanies={total}
        isLoading={isLoading || isAggregationsLoading}
        isError={isAggregationsError}
        isRowsError={isError}
      />
      <div
        data-test="companies-content"
        className="flex flex-col max-h-[calc(var(--app-content-h)-var(--table-bottom-space))] h-full"
      >
        <div
          data-test="companies-header"
          className="flex justify-between p-4 pb-2 h-14"
        >
          <div
            data-test="companies-title"
            className="flex items-center gap-[10px]"
          >
            <EntityLogoIconContainer entityType={EntityType.COMPANY} />
            <Text as="div" variant="lgSubtitleH1" className="p-1">
              {t("companies.title")}
            </Text>
          </div>
          <div
            data-test="companies-actions"
            className="flex items-center gap-2"
          >
            <ExportMenu
              label={t("export")}
              data-test="companies-export-button"
              onExport={exportCompanies}
              isExporting={isExportingCompanies}
            />
            <IconButton
              data-test="companies-create-button"
              onClick={handleOpenCreateModal}
              size="lg"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <DataTableToolbar
          viewType={ViewType.COMPANIES_TABLE}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          filterOptions={companiesFilterOptions}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          defaultColumnsState={defaultColumnsState}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          sortByOptions={companiesSortByOptions}
          defaultSortByValue="name"
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
          onViewActivated={handleViewActivated}
        />
        <ToolbarActions
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          filterOptions={companiesFilterOptions}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          onSortingChange={onSortingChange}
          sortByOptions={companiesSortByOptions}
          defaultSortByValue="name"
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          defaultColumnsState={defaultColumnsState}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          viewType={ViewType.COMPANIES_TABLE}
          onColumnsChange={handleColumnsChange}
          tableId={TABLE_ID}
          customFields={customFields}
          // Undefined while the flag is closed, which is what keeps the
          // Display menu byte-identical for anyone without the feature.
          compareTo={compareToEnabled ? compareTo : undefined}
          onCompareToChange={compareToEnabled ? setCompareTo : undefined}
          defaultCompareTo={DEFAULT_COMPARE_TO}
          className="p-2 pr-4"
        />
        <DataTable
          groupRankResolvers={GROUP_RANK_RESOLVERS}
          key={TABLE_ID}
          dataTest={TABLE_ID}
          columns={columns}
          initialSortBy={sorting}
          manualSorting
          onSortingChange={onSortingChange}
          data={companies}
          headerClassName="border-none"
          stickyLeftColumn
          resizableColumns
          className="h-full"
          isInfiniteScroll
          isLoadingMore={isFetchingNextPage}
          isDataLoading={isLoading || !viewReady}
          hasMore={hasNextPage}
          onLoadMore={handleLoadMore}
          loadingThreshold={100}
          groupBy={groupBy}
          groupingConfig={{
            typeLabel: t("companies.bottomKPI.total"),
            aggregationKey: arrOrMrrField,
            aggregationLabel: arrMrrLabel,
            formatAggregation: (value: number) => displayTotalCurrency(value),
            subtotalAggregations: [
              {
                key: "arrMrr",
                label: t("companies.bottomKPI.arrMrr", {
                  arrMrr: arrMrrLabel
                }),
                aggregate: (rows) =>
                  rows.reduce(
                    (acc, row) => acc + (getArrOrMrrValue(row) || 0),
                    0
                  ),
                fromSums: (sums) => sums[arrOrMrrField] || 0,
                format: (value) => displayTotalCurrency(value)
              },
              {
                key: "avgArrMrr",
                label: t("companies.bottomKPI.avgArrMrr", {
                  arrMrr: arrMrrLabel
                }),
                // Match the server-side avgArrMrr (SQL AVG ignores NULLs): average
                // only over companies that carry the ARR/MRR value, not the full group.
                aggregate: (rows) => {
                  const arrMrrValues = rows
                    .map((row) => getArrOrMrrValue(row))
                    .filter((value) => typeof value === "number");
                  if (arrMrrValues.length === 0) return 0;
                  return (
                    arrMrrValues.reduce((acc, value) => acc + value, 0) /
                    arrMrrValues.length
                  );
                },
                fromSums: (sums, _count, counts) =>
                  counts[arrOrMrrField]
                    ? (sums[arrOrMrrField] || 0) / counts[arrOrMrrField]
                    : 0,
                format: (value) => displayTotalCurrency(value)
              },
              {
                key: "acv",
                label: t("companies.bottomKPI.acv"),
                aggregate: (rows) =>
                  rows.reduce(
                    (acc, row) => acc + (row.annualContractValue || 0),
                    0
                  ),
                fromSums: (sums) => sums.annualContractValue || 0,
                format: (value) => displayTotalCurrency(value)
              },
              {
                key: "tcv",
                label: t("companies.bottomKPI.tcv"),
                aggregate: (rows) =>
                  rows.reduce(
                    (acc, row) => acc + (row.totalContractValue || 0),
                    0
                  ),
                fromSums: (sums) => sums.totalContractValue || 0,
                format: (value) => displayTotalCurrency(value)
              }
            ],
            groups: knownGroups,
            groupSummaries
          }}
          enableColumnReorder
          tableId={TABLE_ID}
          selectedView={selectedView}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          onRowClick={(row: Row<any>) => onRowClick(row.original)}
          filterOptions={companiesFilterOptions}
          filters={filters}
          setFilters={setFilters}
          hasActiveFilters={!isEqual(filters, EmptyFilters)}
          onClearFilters={() => setFilters(EmptyFilters)}
          onClearSearch={() => setSearchTerm("")}
          isError={isError}
          onRetry={refetch}
        />
      </div>
      <CreateCompanyModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        resetPage={resetPage}
        allowDreamer
        candidateData={candidateData}
        onCandidateDataConsumed={clearCandidateData}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.COMPANY}
        onClose={newCloseDrawer}
        userData={userData}
      />
    </div>
  );
};

export default Companies;
